import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  refreshSession,
  type AuthUser,
} from '../api/auth';

type Status = 'checking' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: Status;
  user: AuthUser | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('checking');
  const [user, setUser] = useState<AuthUser | null>(null);

  const resolveSession = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      setStatus('authenticated');
      return;
    } catch {
      // fall through to refresh attempt
    }
    try {
      const refreshed = await refreshSession();
      setUser(refreshed);
      setStatus('authenticated');
    } catch {
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    void resolveSession();
  }, [resolveSession]);

  const doLogin = useCallback(async (username: string, password: string) => {
    const next = await apiLogin(username, password);
    setUser(next);
    setStatus('authenticated');
  }, []);

  const doLogout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // even if server call fails, clear local state
    }
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login: doLogin, logout: doLogout, refresh: resolveSession }),
    [status, user, doLogin, doLogout, resolveSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
